"use client";
import { useEffect, useRef, useState } from "react"

import { CornerDownLeftIcon } from "@/tiptap/icons/corner-down-left-icon"
import { Button } from "@/tiptap/ui-primitive/button"
import { Input } from "@/tiptap/ui-primitive/input"
import { Textarea } from "@/tiptap/ui-primitive/textarea"

const MEDIA_FORM_TABS = ["general", "embed"]

export function MediaForm({
  initialSource = "",
  initialWidth = "640",
  initialHeight = "480",
  initialEmbed = "",
  initialMode = "general",
  autoFocusInput = true,
  submitLabel = "Embed Media",
  invalidMessage = "Enter a media source",
  onSubmit,
}) {
  const [activeTab, setActiveTab] = useState("general")
  const [source, setSource] = useState(initialSource)
  const [width, setWidth] = useState(initialWidth)
  const [height, setHeight] = useState(initialHeight)
  const [embedHtml, setEmbedHtml] = useState(initialEmbed)
  const [error, setError] = useState("")
  const inputRef = useRef(null)
  const tabRefs = useRef({})
  const hasSource = source.trim().length > 0
  const hasEmbedHtml = embedHtml.trim().length > 0
  const canSubmit = activeTab === "embed" ? hasEmbedHtml : hasSource

  useEffect(() => {
    setActiveTab(initialMode === "embed" ? "embed" : "general")
    setSource(initialSource)
    setWidth(initialWidth)
    setHeight(initialHeight)
    setEmbedHtml(initialEmbed)
    setError("")

  }, [initialEmbed, initialHeight, initialMode, initialSource, initialWidth])

  useEffect(() => {
    if (!autoFocusInput) return

    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [activeTab, autoFocusInput])

  const submit = () => {
    const success = onSubmit?.({
      source,
      width,
      height,
      embedHtml,
      mode: activeTab,
    })

    if (!success) {
      setError(invalidMessage)
    }
  }

  const selectTab = (tab) => {
    setActiveTab(tab)
    setError("")
  }

  const handleTabKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return

    event.preventDefault()

    const currentTab = event.target?.closest?.("[data-media-form-tab]")?.dataset.mediaFormTab || activeTab
    const currentIndex = MEDIA_FORM_TABS.indexOf(currentTab)
    const offset = event.key === "ArrowRight" ? 1 : -1
    const nextTab = MEDIA_FORM_TABS[(currentIndex + offset + MEDIA_FORM_TABS.length) % MEDIA_FORM_TABS.length]

    selectTab(nextTab)
    tabRefs.current[nextTab]?.focus()
  }

  const handleSubmitKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.target?.closest?.('[role="tab"]') || !canSubmit) return

    event.preventDefault()
    submit()
  }

  const handleKeyDownCapture = (event) => {
    if (event.target?.closest?.('[role="tab"]')) {
      handleTabKeyDown(event)
    }
    handleSubmitKeyDown(event)

    event.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
  }

  return (
    <div
      data-media-form="true"
      className="tiptap-media-form"
      onKeyDownCapture={handleKeyDownCapture}>
      <div className="tiptap-media-form__tabs" role="tablist" aria-label="Media options">
        {MEDIA_FORM_TABS.map((tab) => (
          <Button
            key={tab}
            ref={(element) => {
              tabRefs.current[tab] = element
            }}
            type="button"
            variant="ghost"
            role="tab"
            aria-selected={activeTab === tab}
            data-media-form-tab={tab}
            data-active-state={activeTab === tab ? "on" : "off"}
            tabIndex={activeTab === tab ? 0 : -1}
            onClick={() => selectTab(tab)}>
            <div className="tiptap-button-text">{tab === "general" ? "General" : "Embed"}</div>
          </Button>
        ))}
      </div>
      {activeTab === "general" ? (
        <>
          <div className="tiptap-media-form__field">
            <label className="tiptap-button-text">Source</label>
            <Input
              ref={inputRef}
              type="text"
              placeholder="Paste a media URL..."
              value={source}
              onChange={(event) => {
                setSource(event.target.value)
                if (error) setError("")
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off" />
          </div>
          <div className="tiptap-media-form__dimensions">
            <div className="tiptap-media-form__field">
              <label className="tiptap-button-text">Width</label>
              <Input
                type="text"
                value={width}
                onChange={(event) => setWidth(event.target.value)} />
            </div>
            <div className="tiptap-media-form__field">
              <label className="tiptap-button-text">Height</label>
              <Input
                type="text"
                value={height}
                onChange={(event) => setHeight(event.target.value)} />
            </div>
          </div>
        </>
      ) : (
        <div className="tiptap-media-form__field">
          <label className="tiptap-button-text">Embed code</label>
          <Textarea
            ref={inputRef}
            placeholder="<iframe ...></iframe>"
            value={embedHtml}
            rows={4}
            className="tiptap-media-form__embed"
            onChange={(event) => {
              setEmbedHtml(event.target.value)
              if (error) setError("")
            }} />
        </div>
      )}
      {error ? <div className="tiptap-button-text">{error}</div> : null}
      <div className="tiptap-media-form__actions">
        <Button
          type="button"
          variant="ghost"
          className="tiptap-media-form-submit"
          disabled={!canSubmit}
          data-disabled={!canSubmit}
          onClick={submit}>
          <CornerDownLeftIcon className="tiptap-button-icon" />
          <div className="tiptap-button-text">{submitLabel}</div>
        </Button>
      </div>
    </div>
  )
}

export default MediaForm
